import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import "../../../styles/admin.css";
import api from "../../../config/URL";

function FreeCashEdit() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [loading, setLoading] = useState(true); // Start with loading true until data is fetched
  const navigate = useNavigate();
  const { id } = useParams();

  const validationSchema = Yup.object({
    event_key: Yup.string().required("*Event key is required"),
    event_name: Yup.string().required("*Event name is required"),
    reward_amount: Yup.number()
      .required("*Reward amount is required")
      .positive("*Reward amount must be a positive number")
      .max(
        99999999.99,
        "*Reward amount must be less than or equal to 99999999.99"
      ),
  });

  const formik = useFormik({
    initialValues: {
      event_key: "",
      event_name: "",
      reward_amount: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      try {
        const response = await api.put(`admin/freecoins/${id}`, values);
        if (response.status === 200) {
          toast.success("Free Cash updated successfully!");
          navigate("/freeCash");
        } else {
          toast.error("Failed to update Free Cash");
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "An error occurred while updating"
        );
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  // Fetch the existing free cash data
  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const response = await api.get(`admin/freecoins/${id}`);
        const freeCoinsData = response.data.data;

        formik.setValues({
          event_key: freeCoinsData.event_key || "",
          event_name: freeCoinsData.event_name || "",
          reward_amount: Number(freeCoinsData.reward_amount) || "",
        });
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error Fetching Data";
        toast.error(errorMessage);
      }
      setLoading(false);
    };
    getData();
  }, [id]);

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        {loading ? (
          <div className="loader-container">
            <div className="loader">
              <svg viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32"></circle>
              </svg>
            </div>
          </div>
        ) : (
          <>
            <div className="card shadow border-0 mb-3">
              <div className="row p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h1 className="h4 ls-tight">Edit Free Cash</h1>
                  <Link to="/freeCash">
                    <button type="button" className="btn btn-light btn-sm">
                      <span>Back</span>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            <div
              className="container card shadow border-0"
              style={{ minHeight: "60vh" }}
            >
              <div className="row mt-3 mb-5 p-3">
                {/* Event Key */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Event Key <span className="text-danger">*</span>
                  </label>
                  <select
                    name="event_key"
                    className={`form-select ${
                      formik.touched.event_key && formik.errors.event_key
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.event_key}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">Select the key</option>
                    <option value="new_user">New User</option>
                  </select>
                  {formik.touched.event_key && formik.errors.event_key && (
                    <div className="invalid-feedback">
                      {formik.errors.event_key}
                    </div>
                  )}
                </div>

                {/* Event Name */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Event Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="event_name"
                    className={`form-control ${
                      formik.touched.event_name && formik.errors.event_name
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.event_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter event name"
                  />
                  {formik.touched.event_name && formik.errors.event_name && (
                    <div className="invalid-feedback">
                      {formik.errors.event_name}
                    </div>
                  )}
                </div>

                {/* Reward Amount */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Reward Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    name="reward_amount"
                    className={`form-control ${
                      formik.touched.reward_amount &&
                      formik.errors.reward_amount
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.reward_amount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter reward amount"
                    step="0.01"
                    min="0"
                  />
                  {formik.touched.reward_amount &&
                    formik.errors.reward_amount && (
                      <div className="invalid-feedback">
                        {formik.errors.reward_amount}
                      </div>
                    )}
                </div>
              </div>

              {/* Submit */}
              <div className="hstack p-2 mt-5">
                <button
                  type="submit"
                  className="btn btn-sm btn-button"
                  disabled={loadIndicator}
                >
                  {loadIndicator && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                  )}
                  Update Free Cash
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </section>
  );
}

export default FreeCashEdit;
